import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Alert,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Dialog as MuiDialog,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ContactApiService from "../services/ContactApiService";
import MessageTemplateService from "../services/MessageTemplateService";
import { filterTemplatesForScreen } from "../utils/templateAppUsage";
import TagService from "../services/TagService";
import ContactImportDialog from "./ContactImportDialog";
import Groups from "../models/Groups";
import { getEffectiveTemplateVariableSamples, emptyTemplateVariablesFromSamples } from "../utils/templateVariableSamples";

const CONTACT_NAME_TOKEN = "{{contact_name}}";
const HEADER_MEDIA_SIZE_LIMITS = {
  image: 5 * 1024 * 1024,
  video: 16 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

const CreateBroadcastDialog = ({ open, onClose, onSave, editBroadcast }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    broadcastName: "",
    selectedTemplate: "",
    scheduleDate: new Date(),
    sendNow: false,
    selectedContacts: [],
  });
  const [templateVariables, setTemplateVariables] = useState({ header: {}, body: {}, footer: {}, buttons: {} });
  /** Per-send HTTPS URL for WhatsApp templates whose header format is IMAGE/VIDEO/DOCUMENT. */
  const [mediaHeaderUrl, setMediaHeaderUrl] = useState("");
  /** Files table / S3 object id from upload — used to delete storage when broadcast is removed. */
  const [mediaHeaderMediaId, setMediaHeaderMediaId] = useState("");
  const [headerMediaUploading, setHeaderMediaUploading] = useState(false);
  const headerMediaFileRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectionType, setSelectionType] = useState("contacts");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [tags, setTags] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importTarget, setImportTarget] = useState("contacts");
  const [csvGroupName, setCsvGroupName] = useState("");
  const [importNotice, setImportNotice] = useState(null);
  const selectedTemplateModel = approvedTemplates.find((t) => t.getId() === formData.selectedTemplate);

  const getTemplateQualityMeta = (template) => {
    const score = (template?.attrs?.templateQualityScore || "").toUpperCase();
    if (!score) return null;
    if (score === "GREEN") return { color: "success", label: "Quality GREEN", warning: null };
    if (score === "YELLOW") {
      return {
        color: "warning",
        label: "Quality YELLOW",
        warning: "Template quality is medium. Marketing pacing may start for some recipients.",
      };
    }
    if (score === "RED") {
      return {
        color: "error",
        label: "Quality RED",
        warning: "Template quality is low. Expect strong pacing/holding or delivery impact.",
      };
    }
    return { color: "default", label: `Quality ${score}`, warning: null };
  };

  const getTemplateStatusMeta = (template) => {
    const event = (template?.attrs?.templateStatusEvent || "").toUpperCase();
    if (!event) return null;
    if (["PAUSED", "DISABLED", "REJECTED", "ARCHIVED", "DELETED", "PENDING_DELETION"].includes(event)) {
      return {
        color: "error",
        label: event,
        warning: `Template is ${event}. Sending can fail or be blocked by Meta.`,
      };
    }
    if (["FLAGGED", "PENDING", "IN_APPEAL", "LIMIT_EXCEEDED", "LOCKED"].includes(event)) {
      return {
        color: "warning",
        label: event,
        warning: `Template is ${event}. This can affect approval or delivery pacing.`,
      };
    }
    return { color: "success", label: event, warning: null };
  };

  const loadContacts = async () => {
    const res = await ContactApiService.getContactsDetails({ limit: 10000 });
    if (res?.success) setContacts(res.data.contacts || []);
  };

  const loadGroups = async () => {
    const res = await ContactApiService.getGroupList({ limit: 1000 });
    if (Array.isArray(res)) setGroups(res);
  };

  const loadTags = async () => {
    const res = await TagService.getAllTags({ limit: 1000 });
    if (Array.isArray(res)) setTags(res);
  };

  useEffect(() => {
    loadContacts();
    MessageTemplateService.getAllTemplates().then((res) => {
      setApprovedTemplates(filterTemplatesForScreen(res, "broadcast"));
    });
    loadGroups();
    loadTags();
  }, []);
  useEffect(() => {
    if (open && editBroadcast) {
      setFormData({
        broadcastName: editBroadcast.attrs.broadcastName || "",
        selectedTemplate: editBroadcast.attrs.selectedTemplate || "",
        scheduleDate: editBroadcast.attrs.scheduleDate ? new Date(editBroadcast.attrs.scheduleDate) : new Date(),
        sendNow: !!editBroadcast.attrs.sendNow,
        selectedContacts: editBroadcast.attrs.selectedContacts || [],
        selectedGroups: editBroadcast.attrs.selectedGroups || [],
        selectedTags: editBroadcast.attrs.selectedTags || [],
      });
      let parsedTv = { header: {}, body: {}, footer: {}, buttons: {} };
      let savedMediaHeader = "";
      let savedMediaHeaderId = "";
      const rawTv = editBroadcast.attrs.templateVariables;
      if (rawTv) {
        try {
          const o = typeof rawTv === "string" ? JSON.parse(rawTv) : rawTv;
          parsedTv = {
            header: o.header || {},
            body: o.body || {},
            footer: o.footer || {},
            buttons: o.buttons || {},
          };
          if (typeof o.media_header_url === "string") savedMediaHeader = o.media_header_url;
          if (typeof o.media_header_id === "string") savedMediaHeaderId = o.media_header_id;
        } catch (e) {
          /* ignore */
        }
      }
      setTemplateVariables(parsedTv);
      setMediaHeaderUrl(savedMediaHeader);
      setMediaHeaderMediaId(savedMediaHeaderId);
      if (editBroadcast.attrs.selectedContacts) {
        const selectedIds = editBroadcast.attrs.selectedContacts.map(c => c.attrs && c.attrs.id);
        setContacts((prev) =>
          prev.map((contact) =>
            selectedIds.includes(contact.attrs.id)
              ? { ...contact, isSelected: true }
              : { ...contact, isSelected: false }
          )
        );
      }
      if (editBroadcast.attrs.selectedGroups) {
        const selectedIds = editBroadcast.attrs.selectedGroups.map(c => c.attrs && c.attrs.id);
        setGroups((prev) =>
          prev.map((group) =>
            selectedIds.includes(group.attrs.id)
              ? { ...group, isSelected: true }
              : { ...group, isSelected: false }
          )
        );
      }

      if (editBroadcast.attrs.selectedTags) {
        const selectedIds = editBroadcast.attrs.selectedTags.map(c => c.attrs && c.attrs.id);
        setTags((prev) =>
          prev.map((group) =>
            selectedIds.includes(group.attrs.id)
              ? { ...group, isSelected: true }
              : { ...group, isSelected: false }
          )
        );
      }

      setActiveStep(0);
    } else if (open && !editBroadcast) {
      setFormData({
        broadcastName: "",
        selectedTemplate: "",
        scheduleDate: new Date(),
        sendNow: false,
        selectedContacts: [],
        selectedGroups: [],
        selectedTags: [],
      });
      setTemplateVariables({ header: {}, body: {}, footer: {}, buttons: {} });
      setMediaHeaderUrl("");
      setMediaHeaderMediaId("");
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
      setGroups((prev) => prev.map((g) => ({ ...g, isSelected: false })));
      setTags((prev) => prev.map((t) => ({ ...t, isSelected: false })));
      setActiveStep(0);
      setImportNotice(null);
    }
  }, [open, editBroadcast]);

  const steps = ["Broadcast Setup", "Contact Selection"];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (field === "selectedTemplate") {
      const selected = approvedTemplates.find(t => t.getId() === value);
      const variableSamples = selected
        ? getEffectiveTemplateVariableSamples(selected)
        : { header: {}, body: {}, footer: {}, buttons: {} };
      setTemplateVariables(emptyTemplateVariablesFromSamples(variableSamples));
      setMediaHeaderUrl("");
      setMediaHeaderMediaId("");
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.broadcastName.trim()) {
      newErrors.broadcastName = "Broadcast name is required";
    }

    if (!formData.selectedTemplate) {
      newErrors.selectedTemplate = "Please select a template";
    }

    if (!formData.sendNow && !formData.scheduleDate) {
      newErrors.scheduleDate = "Please select a schedule date";
    }
    const selected = approvedTemplates.find(t => t.getId() === formData.selectedTemplate);
    const variableSamples = selected
      ? getEffectiveTemplateVariableSamples(selected)
      : { header: {}, body: {}, footer: {}, buttons: {} };
    ["header", "body", "footer", "buttons"].forEach(section => {
      if (variableSamples[section]) {
        Object.keys(variableSamples[section]).forEach(key => {
          if (!templateVariables[section][key] || !templateVariables[section][key].trim()) {
            const label = section === "buttons"
              ? `Please enter the URL suffix for website button {{1}}`
              : `Please enter value for variable {{${key}}} in ${section}`;
            newErrors[`var_${section}_${key}`] = label;
          }
        });
      }
    });

    if (selected?.attrs?.headerType === "media") {
      const u = (mediaHeaderUrl || "").trim();
      if (!/^https:\/\//i.test(u)) {
        newErrors.media_header_url =
          "Upload header media or enter a public HTTPS URL (required for this template).";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const applyContactNameVariable = (section, key) => {
    setTemplateVariables((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: CONTACT_NAME_TOKEN,
      },
    }));
  };

  const handleSelectionTypeChange = (event, newValue) => {
    setSelectionType(newValue);
  };

  const handleConfirmClear = () => {
    if (pendingSelection === "contacts") {
      setGroups((prev) => prev.map((g) => ({ ...g, isSelected: false })));
    } else if (pendingSelection === "groups") {
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
    } else if (pendingSelection === "groupOrContact"){
      setTags((prev) => prev.map((t) => ({ ...t, isSelected: false })));
    }
    setConfirmDialogOpen(false);
    setPendingSelection(null);
  };
  const handleCancelClear = () => {
    setConfirmDialogOpen(false);
    setPendingSelection(null);
  };

  const handleContactSelection = (contactId) => {
    if (selectedGroupsCount > 0) {
      setPendingSelection("contacts");
      setConfirmDialogOpen(true);
      return;
    }
    setContacts((prev) =>
      prev.map((contact) =>
        contact.attrs.id === contactId
          ? { ...contact, isSelected: !contact.isSelected }
          : contact
      )
    );
  };
  const handleGroupSelection = (groupId) => {
    if (selectedContactsCount > 0) {
      setPendingSelection("groups");
      setConfirmDialogOpen(true);
      return;
    }
    setGroups((prev) =>
      prev.map((group) =>
        group.attrs.id === groupId
          ? { ...group, isSelected: !group.isSelected }
          : group
      )
    );
  };

  const handleTagsSelection = (tagId) => {
    if (selectedContactsCount > 0 || selectedGroupsCount > 0) {
      setPendingSelection("groupOrContact");
      setConfirmDialogOpen(true);
      return;
    }
    setTags((prev) =>
      prev.map((tag) =>
        tag.attrs.id === tagId
          ? { ...tag, isSelected: !tag.isSelected }
          : tag
      )
    );
  };
  const handleSelectAll = (event) => {
    if (selectedGroupsCount > 0) {
      setPendingSelection("contacts");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setContacts((prev) => prev.map((contact) => ({ ...contact, isSelected: isChecked })));
  };
  const handleSelectAllGroup = (event) => {
    if (selectedContactsCount > 0) {
      setPendingSelection("groups");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setGroups((prev) => prev.map((group) => ({ ...group, isSelected: isChecked })));
  };
  const handleSelectAllTags = (event) => {
    if (selectedContactsCount > 0 || selectedGroupsCount > 0) {
      setPendingSelection("groupOrContact");
      setConfirmDialogOpen(true);
      return;
    }
    const isChecked = event.target.checked;
    setTags((prev) => prev.map((tag) => ({ ...tag, isSelected: isChecked })));
  };

  const handleSave = () => {
    const selectedContacts = contacts.filter((contact) => contact.isSelected);
    const selectedGroups = groups.filter((group) => group.isSelected);
    const selectedTags = tags.filter((tag) => tag.isSelected);
    const selectedTpl = approvedTemplates.find((t) => t.getId() === formData.selectedTemplate);
    const mergedTemplateVariables = { ...templateVariables };
    if (selectedTpl?.attrs?.headerType === "media") {
      mergedTemplateVariables.media_header_url = (mediaHeaderUrl || "").trim();
      if ((mediaHeaderMediaId || "").trim()) {
        mergedTemplateVariables.media_header_id = mediaHeaderMediaId.trim();
      } else {
        delete mergedTemplateVariables.media_header_id;
      }
    } else {
      delete mergedTemplateVariables.media_header_url;
      delete mergedTemplateVariables.media_header_id;
    }
    const broadcastData = {
      ...formData,
      selectedContacts: selectedContacts,
      selectedGroups: selectedGroups,
      selectedTags: selectedTags,
      templateVariables: mergedTemplateVariables,
    };

    console.log("Saving broadcast:", broadcastData);
    onSave(broadcastData);
    onClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      broadcastName: "",
      selectedTemplate: "",
      scheduleDate: new Date(),
      sendNow: false,
      selectedContacts: [],
      selectedGroups: [],
      selectedTags: [],
    });
    setErrors({});
    setSearchQuery("");
    setContacts((prev) =>
      prev.map((contact) => ({ ...contact, isSelected: false }))
    );
    setGroups((prev) =>
      prev.map((group) => ({ ...group, isSelected: false }))
    );
    setTags((prev) =>
      prev.map((tag) => ({ ...tag, isSelected: false }))
    );
    setImportNotice(null);
    setCsvGroupName("");
    setImportDialogOpen(false);
    setImportTarget("contacts");
    setMediaHeaderUrl("");
    setMediaHeaderMediaId("");
    onClose();
  };

  const openImportDialog = (target) => {
    if (target === "groups" && !csvGroupName.trim()) {
      setImportNotice({
        type: "error",
        message: "Enter a group name first, then import CSV to create that group.",
      });
      return;
    }
    setImportTarget(target);
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = async (success = 0, failed = 0, meta = {}) => {
    setImportDialogOpen(false);
    const importedContactIds = meta?.importedContactIds || [];

    if (!success) {
      return;
    }

    if (importTarget === "contacts") {
      await loadContacts();
      const importedSet = new Set(importedContactIds);
      setContacts((prev) => prev.map((c) => ({
        ...c,
        isSelected: importedSet.has(c.attrs.id) ? true : c.isSelected,
      })));
      setImportNotice({
        type: "success",
        message: `${success} contacts imported${failed ? `, ${failed} failed` : ""}. Imported contacts are now selected.`,
      });
      return;
    }

    const groupName = csvGroupName.trim();
    if (!groupName || importedContactIds.length === 0) {
      setImportNotice({
        type: "warning",
        message: "Contacts imported, but group was not created because no imported contact ids were returned.",
      });
      return;
    }

    const groupModel = new Groups()
      .setName(groupName)
      .setDescription(`Imported from CSV during broadcast creation`)
      .setContactsList(importedContactIds.map((id) => ({ id })));

    await ContactApiService.createGroup(groupModel);
    await loadGroups();

    setGroups((prev) => prev.map((group) => ({
      ...group,
      isSelected: group.attrs.name === groupName,
    })));
    setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
    setTags((prev) => prev.map((t) => ({ ...t, isSelected: false })));
    setSelectionType("groups");
    setImportNotice({
      type: "success",
      message: `Imported ${success} contacts and created group "${groupName}".`,
    });
  };

  const getTagColor = (tag) => {
    return tag || "#757575";
  };
console.log('contacts', contacts);

  const filteredContacts = contacts?.filter(
    (contact) =>
      contact.attrs.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.attrs.phoneNumber.includes(searchQuery)
  );
  const filteredGroups = groups.filter(
    (group) =>
      group.attrs.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredTags = tags.filter(
    (tag) =>
      tag.attrs.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedContactsCount = contacts.filter(
    (contact) => contact.isSelected
  ).length;
  const selectedGroupsCount = groups.filter(
    (group) => group.isSelected
  ).length;
  const selectedTagsCount = tags.filter(
    (tag) => tag.isSelected
  ).length;
  const isAllSelected =
    contacts.length > 0 && selectedContactsCount === contacts.length;
  const isIndeterminate =
    selectedContactsCount > 0 && selectedContactsCount < contacts.length;
    const isAllGroupSelected =
    groups.length > 0 && selectedGroupsCount === groups.length;
  const isGroupIndeterminate =
    selectedGroupsCount > 0 && selectedGroupsCount < groups.length;
    const isAllTagSelected =
    tags.length > 0 && selectedTagsCount === tags.length;
  const isTagIndeterminate =
    selectedTagsCount > 0 && selectedTagsCount < tags.length;

  const renderStep1 = () => {
    const selected = selectedTemplateModel;
    const variableSamples = selected
      ? getEffectiveTemplateVariableSamples(selected)
      : { header: {}, body: {}, footer: {}, buttons: {} };
    const ctaButtons = selected?.attrs?.ctaButtons || [];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#333", mb: 2, fontFamily: 'Poppins, sans-serif'}}
            >
              Broadcast Details
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Broadcast Name"
                value={formData.broadcastName}
                onChange={(e) =>
                  handleInputChange("broadcastName", e.target.value)
                }
                error={!!errors.broadcastName}
                helperText={errors.broadcastName}
                fullWidth
                size="small"
                placeholder="Enter broadcast name"
              />

              <FormControl
                fullWidth
                size="small"
                error={!!errors.selectedTemplate}
              >
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={formData.selectedTemplate}
                  onChange={(e) =>
                    handleInputChange("selectedTemplate", e.target.value)
                  }
                  label="Select Template"
                >
                  {approvedTemplates.map((template) => (
                    <MenuItem key={template.getId()} value={template.getId()}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {template.getTemplateName()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.getCategory()} • {template.getLanguage()}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedTemplate && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.selectedTemplate}
                  </Typography>
                )}
              </FormControl>
              {selectedTemplateModel ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {getTemplateQualityMeta(selectedTemplateModel) ? (
                      <Chip
                        size="small"
                        color={getTemplateQualityMeta(selectedTemplateModel).color}
                        label={getTemplateQualityMeta(selectedTemplateModel).label}
                        variant="outlined"
                      />
                    ) : null}
                    {getTemplateStatusMeta(selectedTemplateModel) ? (
                      <Chip
                        size="small"
                        color={getTemplateStatusMeta(selectedTemplateModel).color}
                        label={`Status ${getTemplateStatusMeta(selectedTemplateModel).label}`}
                        variant="outlined"
                      />
                    ) : null}
                    {selectedTemplateModel?.attrs?.templateStatusCategory ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Category ${selectedTemplateModel.attrs.templateStatusCategory}`}
                      />
                    ) : null}
                  </Box>
                  {getTemplateQualityMeta(selectedTemplateModel)?.warning ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {getTemplateQualityMeta(selectedTemplateModel).warning}
                    </Alert>
                  ) : null}
                  {getTemplateStatusMeta(selectedTemplateModel)?.warning ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {getTemplateStatusMeta(selectedTemplateModel).warning}
                    </Alert>
                  ) : null}
                </Box>
              ) : null}

              {selectedTemplateModel?.attrs?.headerType === "media" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Header media (this broadcast)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Meta must be able to fetch this file over HTTPS (upload uses your configured storage URL).
                  </Typography>
                  <input
                    ref={headerMediaFileRef}
                    type="file"
                    hidden
                    accept={
                      selectedTemplateModel?.attrs?.headerMediaType === "video"
                        ? "video/*"
                        : selectedTemplateModel?.attrs?.headerMediaType === "document"
                          ? "application/pdf,.pdf"
                          : "image/jpeg,.jpg,.jpeg,image/png,.png"
                    }
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (e.target) e.target.value = "";
                      if (!file) return;
                      const mediaType = selectedTemplateModel?.attrs?.headerMediaType || "image";
                      const limit = HEADER_MEDIA_SIZE_LIMITS[mediaType];
                      if (limit && file.size > limit) {
                        const sizeMb = Math.floor(limit / (1024 * 1024));
                        setErrors((prev) => ({
                          ...prev,
                          media_header_url: `${mediaType} size must be ${sizeMb} MB or less (Meta limit).`,
                        }));
                        return;
                      }
                      setHeaderMediaUploading(true);
                      try {
                        const result = await MessageTemplateService.uploadMediaNode(file);
                        const row0 = result?.data?.[0];
                        const url = row0?.url;
                        if (url) {
                          setMediaHeaderUrl(url);
                          setMediaHeaderMediaId(row0?.id ? String(row0.id) : "");
                          if (errors.media_header_url) {
                            setErrors((prev) => ({ ...prev, media_header_url: "" }));
                          }
                        }
                      } catch (err) {
                        console.error(err);
                      } finally {
                        setHeaderMediaUploading(false);
                      }
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={headerMediaUploading}
                      onClick={() => headerMediaFileRef.current?.click()}
                    >
                      {headerMediaUploading ? "Uploading…" : "Upload file"}
                    </Button>
                  </Box>
                  <TextField
                    label="HTTPS URL"
                    value={mediaHeaderUrl}
                    onChange={(e) => {
                      setMediaHeaderUrl(e.target.value);
                      setMediaHeaderMediaId("");
                      if (errors.media_header_url) {
                        setErrors((prev) => ({ ...prev, media_header_url: "" }));
                      }
                    }}
                    error={!!errors.media_header_url}
                    helperText={
                      errors.media_header_url || "Required so WhatsApp can download the header asset for each recipient"
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
              ) : null}

              {/* Variable input fields for header/body/footer */}
              {["header", "body", "footer"].map((section) => (
                variableSamples[section] && Object.keys(variableSamples[section]).length > 0 && (
                  <Box key={section} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {section.charAt(0).toUpperCase() + section.slice(1)} Variables
                    </Typography>
                    {Object.keys(variableSamples[section]).map((key) => (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          "& .contact-name-action": {
                            opacity: 0,
                            pointerEvents: "none",
                            transition: "opacity 0.18s ease",
                          },
                          "&:hover .contact-name-action, &:focus-within .contact-name-action": {
                            opacity: 1,
                            pointerEvents: "auto",
                          },
                        }}
                      >
                        <TextField
                          label={`Value for {{${key}}}`}
                          value={templateVariables[section][key] || ""}
                          onChange={(e) =>
                            setTemplateVariables((prev) => ({
                              ...prev,
                              [section]: {
                                ...prev[section],
                                [key]: e.target.value,
                              },
                            }))
                          }
                          error={!!errors[`var_${section}_${key}`]}
                          helperText={
                            errors[`var_${section}_${key}`]
                              || `Enter value for {{${key}}} (or use contact name token)`
                          }
                          fullWidth
                          size="small"
                        />
                        <Tooltip title="Use contact name variable">
                          <IconButton
                            className="contact-name-action"
                            size="small"
                            onClick={() => applyContactNameVariable(section, key)}
                            sx={{
                              mt: 0.5,
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              border: "1px solid #90caf9",
                              color: "#1565c0",
                              backgroundColor: "#f5f9ff",
                              "&:hover": {
                                borderColor: "#1976d2",
                                backgroundColor: "#eaf2ff",
                              },
                            }}
                          >
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )
              ))}

              {variableSamples.buttons && Object.keys(variableSamples.buttons).length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Button Variables
                  </Typography>
                  {Object.keys(variableSamples.buttons).map((key) => {
                    const btn = ctaButtons[Number(key)];
                    const btnLabel = btn?.label?.trim() || `Button ${Number(key) + 1}`;
                    const urlPattern = btn?.value || "";
                    return (
                      <Box
                        key={`button-${key}`}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          "& .contact-name-action": {
                            opacity: 0,
                            pointerEvents: "none",
                            transition: "opacity 0.18s ease",
                          },
                          "&:hover .contact-name-action, &:focus-within .contact-name-action": {
                            opacity: 1,
                            pointerEvents: "auto",
                          },
                        }}
                      >
                        <TextField
                          label={`URL suffix for "${btnLabel}" ({{1}})`}
                          value={templateVariables.buttons[key] || ""}
                          onChange={(e) =>
                            setTemplateVariables((prev) => ({
                              ...prev,
                              buttons: {
                                ...prev.buttons,
                                [key]: e.target.value,
                              },
                            }))
                          }
                          error={!!errors[`var_buttons_${key}`]}
                          helperText={
                            errors[`var_buttons_${key}`]
                              || (urlPattern
                                ? `Replaces {{1}} in ${urlPattern}`
                                : "Value inserted into the website button URL at send time")
                          }
                          fullWidth
                          size="small"
                        />
                        <Tooltip title="Use contact name variable">
                          <IconButton
                            className="contact-name-action"
                            size="small"
                            onClick={() => applyContactNameVariable("buttons", key)}
                            sx={{
                              mt: 0.5,
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              border: "1px solid #90caf9",
                              color: "#1565c0",
                              backgroundColor: "#f5f9ff",
                              "&:hover": {
                                borderColor: "#1976d2",
                                backgroundColor: "#eaf2ff",
                              },
                            }}
                          >
                            <PersonIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendNow}
                    onChange={(e) =>
                      handleInputChange("sendNow", e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Send Now"
                sx={{ alignSelf: "flex-start", fontFamily: 'Poppins, sans-serif' }}
              />

              {!formData.sendNow && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Schedule Date & Time"
                    value={formData.scheduleDate}
                    onChange={(newValue) =>
                      handleInputChange("scheduleDate", newValue)
                    }
                    minutesStep={60}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={!!errors.scheduleDate}
                        helperText={errors.scheduleDate}
                      />
                    )}
                    minDateTime={new Date()}
                  />
                </LocalizationProvider>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Tabs value={selectionType} onChange={handleSelectionTypeChange} sx={{ mb: 2 }}>
        <Tab label="Contacts" value="contacts" />
        <Tab label="Groups" value="groups" />
        <Tab label="Tags" value="tags" />
      </Tabs>
      {selectionType === "contacts" ? (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Recipients
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="outlined" size="small" onClick={() => openImportDialog("contacts")}>
                  Import CSV Contacts
                </Button>
              </Box>
              <TextField
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isIndeterminate}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Phone Number
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Tags
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContacts?.map((contact) => (
                      <TableRow key={contact.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={contact.isSelected}
                            onChange={() => handleContactSelection(contact.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2", fontSize: "0.875rem" }}>
                              <PersonIcon />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {contact?.attrs.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{contact.attrs.phoneNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {contact?.attrs.tags.map((tag) => (
                              <Chip
                                key={tag.getId()}
                                label={tag.getName()}
                                size="small"
                                sx={{
                                  backgroundColor: `${getTagColor(tag.getColor())}15`,
                                  color: getTagColor(tag.getColor()),
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedContactsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedContactsCount} contact(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : selectionType === "groups" ? (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Groups
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  label="Group name for CSV import"
                  placeholder="e.g. Imported festival list"
                  value={csvGroupName}
                  onChange={(e) => setCsvGroupName(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" size="small" onClick={() => openImportDialog("groups")}>
                  Import CSV & Create Group
                </Button>
              </Box>
              <TextField
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isGroupIndeterminate}
                          checked={isAllGroupSelected}
                          onChange={handleSelectAllGroup}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Contacts Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGroups?.map((group) => (
                      <TableRow key={group.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={group.isSelected}
                            onChange={() => handleGroupSelection(group.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {group?.attrs.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{group.attrs.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{group.attrs.contactsCount}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedGroupsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedGroupsCount} group(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : selectionType === "tags" && (
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
              Select Tags
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "#f5f5f5" }}>
                        <Checkbox
                          indeterminate={isTagIndeterminate}
                          checked={isAllTagSelected}
                          onChange={handleSelectAllTags}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                        Contacts Count
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTags?.map((tag) => (
                      <TableRow key={tag.attrs.id} sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={tag.isSelected}
                            onChange={() => handleTagsSelection(tag.attrs.id)}
                            sx={{ color: "#666" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {tag?.attrs.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tag.attrs.description}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{tag.attrs.contactsCount}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectedTagsCount > 0 && (
                <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                  <Typography variant="body2">
                    {selectedTagsCount} tag(s) selected for broadcast
                  </Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      {importNotice ? (
        <Alert severity={importNotice.type} sx={{ mt: 1 }}>
          {importNotice.message}
        </Alert>
      ) : null}
      <MuiDialog open={confirmDialogOpen} onClose={handleCancelClear}>
        <DialogTitle>Clear Selection?</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingSelection === "contacts"
              ? "Selecting contacts will clear selected groups. Continue?"
              : pendingSelection === "groups" ? "Selecting groups will clear selected contacts. Continue?"
            : pendingSelection === "groupOrContact" ? "Selecting tags will clear selected contacts and groups. Continue?" : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClear}>Cancel</Button>
          <Button onClick={handleConfirmClear} color="primary">Continue</Button>
        </DialogActions>
      </MuiDialog>
      <ContactImportDialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        onImport={async ({ name, phone }) => {
          const response = await ContactApiService.bulkImportContacts([
            { name, phone_number: phone },
          ]);
          const row = response?.results?.find((r) => r.id);
          return {
            success: Boolean(row?.id),
            contactId: row?.id || null,
          };
        }}
        existingContacts={contacts}
        loadingExistingContacts={false}
        groupHint={importTarget === "groups" ? `Imported contacts will be added to group "${csvGroupName}".` : null}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", fontFamily: 'Poppins, sans-serif' }}>
            Create Broadcast
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 4 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 ? renderStep1() : renderStep2()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
        {activeStep === 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
            }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {activeStep === 0 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={selectionType === 'contacts' ? selectedContactsCount === 0 : selectionType === 'groups' ? selectedGroupsCount === 0 : selectionType === 'tags' ? selectedTagsCount === 0 : false}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "#999",
                fontFamilys: 'Poppins, sans-serif',
              },
            }}
          >
            Create Broadcast ({selectionType === 'contacts' ? selectedContactsCount : selectionType === 'groups' ? selectedGroupsCount : selectionType === 'tags' ? selectedTagsCount : 0})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBroadcastDialog;
